/**
 * CaseEmailsPanel — Phase 4 Email-to-case (G.15). Shows a case's ingested email history and lets
 * staff trigger an ingest run. Staff-only view (court emails are never client-visible).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  MenuItem,
  TextField,
} from '@mui/material';
import CaseEmailApi, { CaseEmailDto } from '../../api/CaseEmailApi';

interface CaseEmailsPanelProps {
  Ticket_guid: string;
}

const CaseEmailsPanel: React.FC<CaseEmailsPanelProps> = ({ Ticket_guid }) => {
  const [emails, setEmails] = useState<CaseEmailDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [mailbox, setMailbox] = useState<'court' | 'client'>('court');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!Ticket_guid) return;
    setLoading(true);
    setError(null);
    try {
      setEmails(await CaseEmailApi.listForCase(Ticket_guid));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load case emails');
    } finally {
      setLoading(false);
    }
  }, [Ticket_guid]);

  useEffect(() => {
    load();
  }, [load]);

  const ingest = async () => {
    setIngesting(true);
    setError(null);
    setInfo(null);
    try {
      const s = await CaseEmailApi.ingest(mailbox);
      setInfo(`Scanned ${s.scanned}, matched ${s.matched}, stored ${s.stored}, skipped ${s.skippedExisting}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to ingest emails');
    } finally {
      setIngesting(false);
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', flex: 1 }}>
          Emails on this case ({emails.length})
        </Typography>
        <TextField select size="small" label="Mailbox" value={mailbox} onChange={(e) => setMailbox(e.target.value as 'court' | 'client')} sx={{ minWidth: 140 }}>
          <MenuItem value="court">Court</MenuItem>
          <MenuItem value="client">Client</MenuItem>
        </TextField>
        <Button variant="outlined" onClick={ingest} disabled={ingesting}>
          {ingesting ? 'Ingesting…' : 'Ingest now'}
        </Button>
      </Stack>

      {info && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : emails.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 2 }}>
          No emails have been tied to this case yet.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {emails.map((em) => (
            <Card key={em.CaseEmail_guid} variant="outlined">
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                    {em.CaseEmail_Subject || '(no subject)'}
                  </Typography>
                  <Chip label={em.CaseEmail_Direction} size="small" variant="outlined" />
                  {em.CaseEmail_MatchedBy && <Chip label={em.CaseEmail_MatchedBy} size="small" />}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  From {em.CaseEmail_From || 'unknown'} ·{' '}
                  {em.CaseEmail_ReceivedAt ? new Date(em.CaseEmail_ReceivedAt).toLocaleString('en-US') : ''} · {em.CaseEmail_Mailbox}
                </Typography>
                {em.CaseEmail_Preview && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {em.CaseEmail_Preview}
                  </Typography>
                )}
                {em.CaseEmail_CdlRefusal && (
                  <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                    No answer was sent. {em.CaseEmail_CdlRefusalExplanation || em.CaseEmail_CdlRefusal}
                  </Alert>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default CaseEmailsPanel;
